import { useEffect, useId, useState } from 'react';

import { type ChecklistItem, type Content, replaceChecklist } from '../api.ts';
import { describeError } from '../data.tsx';
import { Icon } from '../Icon.tsx';
import { useToast } from '../toast.tsx';
import * as wording from '../wording.ts';

/** Design reference § Validation: at most 30 items of 1–120 characters. */
export const MAX_CHECKLIST_ITEMS = 30;
export const MAX_CHECKLIST_TEXT_LENGTH = 120;

/**
 * A content's checklist (US-018): tick, add (Enter), rename in place and
 * delete. Every change sends the whole list through
 * `PUT /api/contents/:id/checklist` and takes the server's list back, so a
 * new item has its id before anything else can touch it. Standalone, for the
 * drawer and the content page (US-031).
 */
export function ChecklistEditor({
  contentId,
  items,
  onSaved,
}: {
  readonly contentId: string;
  readonly items: readonly ChecklistItem[];
  /** The content as the server answered it, after a save. */
  readonly onSaved?: (content: Content) => void;
}) {
  const toast = useToast();
  const id = useId();
  const [list, setList] = useState<readonly ChecklistItem[]>(items);
  const [adding, setAdding] = useState('');
  const [saving, setSaving] = useState(false);
  /** Bumped by a refused save, so every item text shows the list as it stands again. */
  const [round, setRound] = useState(0);

  // A save, a poll or another tab may change the list; follow it.
  useEffect(() => {
    setList(items);
  }, [items]);

  const save = async (next: readonly (Omit<ChecklistItem, 'id'> & { id?: string })[]): Promise<boolean> => {
    const before = list;
    // Shown at once; an item still without an id is shown once the server has named it.
    setList(next.filter((item): item is ChecklistItem => item.id !== undefined));
    setSaving(true);
    try {
      const content = await replaceChecklist(contentId, next);
      setList(content.checklist);
      onSaved?.(content);
      return true;
    } catch (cause: unknown) {
      setList(before);
      setRound((value) => value + 1);
      toast.error(describeError(cause));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const add = async (): Promise<void> => {
    const text = adding.trim();
    if (text === '' || list.length >= MAX_CHECKLIST_ITEMS) return;
    if (await save([...list, { text, done: false }])) setAdding('');
  };

  const rename = (item: ChecklistItem, text: string): void => {
    const trimmed = text.trim();
    if (trimmed === '' || trimmed === item.text) return;
    void save(list.map((other) => (other.id === item.id ? { ...other, text: trimmed } : other)));
  };

  const done = list.filter((item) => item.done).length;

  return (
    <div className="checklist">
      <h3 className="checklist__title" id={`${id}-title`}>
        {wording.CHECKLIST}
        {list.length > 0 && <span className="board__count">{wording.checklistProgress(done, list.length)}</span>}
      </h3>
      {list.length === 0 ? (
        <p className="field__hint">{wording.CHECKLIST_EMPTY}</p>
      ) : (
        <ul className="checklist__items" aria-labelledby={`${id}-title`}>
          {list.map((item) => (
            <li key={item.id} className="checklist__item">
              <input
                type="checkbox"
                checked={item.done}
                aria-label={item.text}
                onChange={(event) => {
                  const checked = event.target.checked;
                  void save(list.map((other) => (other.id === item.id ? { ...other, done: checked } : other)));
                }}
              />
              <ChecklistText
                // A new key after each save or refusal puts the text the list holds back.
                key={`${item.id}:${item.text}:${String(round)}`}
                text={item.text}
                done={item.done}
                onCommit={(text) => {
                  rename(item, text);
                }}
              />
              <button
                type="button"
                className="button button--quiet button--small button--icon"
                title={wording.CHECKLIST_DELETE}
                aria-label={`${wording.CHECKLIST_DELETE}: ${item.text}`}
                onClick={() => {
                  void save(list.filter((other) => other.id !== item.id));
                }}
              >
                <Icon name="trash" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {list.length >= MAX_CHECKLIST_ITEMS ? (
        <p className="field__hint">{wording.CHECKLIST_FULL}</p>
      ) : (
        <input
          className="field__input"
          type="text"
          maxLength={MAX_CHECKLIST_TEXT_LENGTH}
          placeholder={wording.CHECKLIST_ADD_PLACEHOLDER}
          aria-label={wording.CHECKLIST_ADD}
          value={adding}
          disabled={saving}
          onChange={(event) => {
            setAdding(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            void add();
          }}
        />
      )}
    </div>
  );
}

/** One item's text, edited where it stands: Enter or leaving it saves, Esc puts it back. */
function ChecklistText({
  text,
  done,
  onCommit,
}: {
  readonly text: string;
  readonly done: boolean;
  readonly onCommit: (text: string) => void;
}) {
  const [value, setValue] = useState(text);
  return (
    <input
      className={`checklist__text${done ? ' checklist__text--done' : ''}`}
      type="text"
      maxLength={MAX_CHECKLIST_TEXT_LENGTH}
      aria-label={wording.CHECKLIST}
      value={value}
      onChange={(event) => {
        setValue(event.target.value);
      }}
      onBlur={() => {
        // An emptied item is put back rather than deleted by accident; Delete is the button.
        if (value.trim() === '') setValue(text);
        else onCommit(value);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.currentTarget.blur();
        } else if (event.key === 'Escape') {
          // Esc here undoes the edit; it does not also close the drawer.
          event.stopPropagation();
          setValue(text);
          const input = event.currentTarget;
          window.setTimeout(() => {
            input.blur();
          }, 0);
        }
      }}
    />
  );
}
